import { useQuery } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import { api, type Language } from '@/lib/api';

// Hard-coded fallback — the original 12 cells. Used as the initial value
// AND if GET /api/catalogue ever fails, so the picker can never break the
// cockpit. The catalogue (seeded from these same 12) supersedes it once
// it loads, and adds any newly-onboarded bank's cells.
const FALLBACK_CELLS: ReadonlyArray<readonly [string, string]> = [
  ['ENBD_CC_PreDue', 'ENBD · CC · PreDue'],
  ['ENBD_CC_BucketX', 'ENBD · CC · BucketX'],
  ['ENBD_CC_30DPD', 'ENBD · CC · 30DPD'],
  ['EI_CC_PreDue', 'EI · CC · PreDue'],
  ['EI_CC_BucketX', 'EI · CC · BucketX'],
  ['EI_CC_30DPD', 'EI · CC · 30DPD'],
  ['ENBD_PL_PreDue', 'ENBD · PL · PreDue'],
  ['ENBD_PL_BucketX', 'ENBD · PL · BucketX'],
  ['ENBD_PL_30DPD', 'ENBD · PL · 30DPD'],
  ['EI_PL_PreDue', 'EI · PL · PreDue'],
  ['EI_PL_BucketX', 'EI · PL · BucketX'],
  ['EI_PL_30DPD', 'EI · PL · 30DPD'],
];

export function CellLangPicker({
  cell,
  language,
  onChange,
}: {
  cell: string;
  language: Language;
  onChange: (next: { cell: string; language: Language }) => void;
}) {
  // Catalogue-driven, with the hard-coded list as a safe default. If the
  // query errors, we keep the fallback — the picker stays usable.
  const catQ = useQuery({
    queryKey: ['catalogue'],
    queryFn: api.getCatalogue,
    staleTime: 60_000,
  });

  const cells: ReadonlyArray<readonly [string, string]> =
    catQ.data && catQ.data.cells.length > 0
      ? catQ.data.cells
          .filter((c) => c.enabled)
          .map((c) => [c.slug, `${c.bank} · ${c.product} · ${c.bucket}`] as const)
      : FALLBACK_CELLS;

  return (
    <div className="flex flex-wrap items-end gap-4">
      <Field label="Cell">
        <select
          value={cell}
          onChange={(e) => onChange({ cell: e.target.value, language })}
          className={fieldCls}
        >
          {cells.map(([slug, label]) => (
            <option key={slug} value={slug}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Language">
        <select
          value={language}
          onChange={(e) => onChange({ cell, language: e.target.value as Language })}
          className={fieldCls}
        >
          <option value="EN">EN</option>
          <option value="AR">AR</option>
        </select>
      </Field>
    </div>
  );
}

const fieldCls = cn(
  'h-9 rounded-md border border-input bg-card px-3 text-sm shadow-sm',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
);

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
